import { HttpClient, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Inject, Injectable } from "@angular/core";
import { SessionStorage } from "ngx-webstorage";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { FormStatusPipe } from "src/app/shared/pipes";
import { IAgreementTextInfo, IChallengeTypeInfo, IClearinghouseInfo, IDenialTypeInfo, IDesigneeInfo, IEmailNotificationCustomization, IEmailNotificationCustomizationChoice, IExtendedStudentInfo, INotificationsInfo, IParentGuardian, IProgramContactInformation, IProgramInformation, ISchoolInfo, IStudentApplication } from "./multiplePathways";
import { ISearchStudentResult, IUserSearchResult } from "../../../features/studentsearch/studentsearch";


@Injectable({
    providedIn: 'root',
})
export class MultiplePathwaysSerivce {
    baseUrl = '';

    // Save app specific, non-secure info for restoring after the wipe.
    @SessionStorage(void 0, [] as string[]) _AllSchoolYears!: string[];
    @SessionStorage(void 0, [] as string[]) _AvailableApplicationSchoolYears?: string[] | null;
    @SessionStorage(void 0, [] as IChallengeTypeInfo[]) _AvailableChallengeTypes?: IChallengeTypeInfo[] | null;
    @SessionStorage(void 0, [] as IDenialTypeInfo[]) _AvailableDenialTypes?: IDenialTypeInfo[] | null;
    @SessionStorage(void 0, [] as IProgramInformation[]) _AvailablePrograms?: IProgramInformation[] | null;
    @SessionStorage(void 0, [] as ISchoolInfo[]) _AvailableSchools?: ISchoolInfo[] | null;
    @SessionStorage(void 0, '' as string) _CurrentApplicationSchoolYear!: string;
    @SessionStorage(void 0, '' as string) _CurrentSchoolYear?: string | null;
    @SessionStorage(void 0, '' as string) _SelectedSchoolYear!: string | null;
    
    DevSiteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get DevSite(): boolean {
        return this.DevSiteSubject.value;
    }

    public set DevSite(value: boolean) {
        this.DevSiteSubject.next(value);
    }

    // #endregion

    formStatusChange: EventEmitter<any> = new EventEmitter<any>();
    newApplication: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private http: HttpClient,
        private formstatuspipe: FormStatusPipe, 
        @Inject('BASE_URL') baseUrl: string) 
    { 
        this.baseUrl = baseUrl;

        this.DevSite = window.location.hostname.indexOf('localhost') >= 0 || window.location.hostname.indexOf('multiplepathwaysdev') >= 0;
    }

    // #region Add Clearinghouse Team Member Service Call

    AddClearinghouseTeamMember(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/AddClearinghouseTeamMember', { params })
            .pipe(map( (clearinghouseTeamMemberList: IClearinghouseInfo[]) => clearinghouseTeamMemberList));
    }

    // #endregion

    // #region Add District Staff Member Service Call

    AddDistrictStaffMember(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/AddDistrictStaffMember', { params })
            .pipe(map( (clearinghouseInfoList: IClearinghouseInfo[]) => clearinghouseInfoList));
    }

    // #endregion

    // #region Add Program Contact Service Call

    AddProgramContact(AuthToken: string, CurrentRole: string, ProgramID?: number, ProgramContactUserID?: number, SAPID?: string, PositionNo?: string, LocationNumber?: string, PrimaryContact?: boolean) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramID: ProgramID || '',
            ProgramContactUserID: ProgramContactUserID || '',
            SAPID: SAPID || '',
            PositionNo: PositionNo || '',
            LocationNumber: LocationNumber || '',
            PrimaryContact: PrimaryContact != null ? PrimaryContact : ''
        };

        return this.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/AddProgramContact', { params })
            .pipe(
                map( (pgms: IProgramInformation[]) => {
                    if (pgms && !!pgms.length) {
                        pgms.forEach(p => {
                            p.OriginalProgramName = p.Description;
    
                            if (p.AgreementText) {
                                p.AgreementText.DateCreated = p.AgreementText.DateCreated ? new Date(<unknown>p.AgreementText.DateCreated as string) : null;
                            }
                        });
                    }

                    return pgms;
                } 
            ));
    }

    // #endregion

    // #region Add Program To Application Service Call

    AddProgramToApplication(AuthToken: string, CurrentRole: string, PathwaysReferralID: number | null, ProgramID: number | null, AgreementTextID: number | null) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || '',
            ProgramID: ProgramID || '',
            AgreementTextID: AgreementTextID || ''
        }
        
        return this.http.get<number>('Api/Services/MultiplePathways/AddProgramToApplication', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region Add Reader Service Call

    AddReader(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/AddReader', { params })
            .pipe(map( (clearingData: IClearinghouseInfo[]) => clearingData));
    }

    // #endregion

    // #region Approve Application Service Call

    ApproveApplication(AuthToken: string, CurrentRole: string, Application: IStudentApplication): Observable<number> {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: Application.PathwaysReferralID || '',
            ResolutionSchoolYear: Application.ResolutionSchoolYear || '',
            ProgramStartDate: Application.ProgramStartDate != null ? Application.ProgramStartDate.toJSON() : '',
            ProgramLocation: Application.ProgramLocation || '',
            Conditional: (Application.ConditionalApprovalNote ? true : false),
            ConditionalNote: Application.ConditionalApprovalNote || '',
            ProgramsApplied: Application.ProgramsApplied || ''
        }

        return this.http.post<number>('Api/Services/MultiplePathways/ApproveApplication', params )
            .pipe(map( (apps: number) => apps));
    }

    // #endregion

    // #region Deny Application Service Call

    DenyApplication(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, DenialTypeID: number, DenialNote: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || '',
            DenialTypeID: DenialTypeID || '',
            DenialNote: DenialNote || ''
        }

        return this.http.post<number>('Api/Services/MultiplePathways/DenyApplication', params)
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region Deny Program Service Call

    DenyProgram(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ReferralProgramChoiceID: number, DenialNote: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || '',
            ReferralProgramChoiceID: ReferralProgramChoiceID || '',
            DenialNote: DenialNote || ''
        }
        
        return this.http.get<number>('Api/Services/MultiplePathways/DenyProgram', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region Get Agreement Text Service Call

    GetAgreementText(AuthToken: string, AgreementTextID: number) {
        const params = {
            AuthToken: AuthToken || '',
            AgreementTextID: AgreementTextID || ''
        }

        return this.http.get<IAgreementTextInfo>('Api/Services/MultiplePathways/GetAgreementText', { params })
            .pipe(
                map( (texts: IAgreementTextInfo) => {
                    texts.DateCreated = texts.DateCreated ? new Date(texts.DateCreated.toString()) : null;
                    return texts;
                } 
            ));
    }

    // #endregion

    // #region Get All Clearinghouse Team Members Service Call

    GetAllClearinghouseTeamMembers(AuthToken: string, CurrentRole?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/GetAllClearinghouseTeamMembers', { params })
            .pipe(map( (clearing: IClearinghouseInfo[]) => clearing ));
    };

    // #endregion

    // #region Get All District Staff Members Service Call

    GetAllDistrictStaffMembers(AuthToken: string, CurrentRole?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/GetAllDistrictStaffMembers', { params })
            .pipe(map( (clearing: IClearinghouseInfo[]) => clearing ));
    };

    // #endregion

    // #region Get All Readers Service Call

    GetAllReaders(AuthToken: string, CurrentRole?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || ''
        }

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/GetAllReaders', { params })            
            .pipe(map( (clearing: IClearinghouseInfo[]) => clearing ));
    };

    // #endregion

    // #region Get All Schools Service Call

    GetAllSchools(AuthToken: string, IncludeCharter?: boolean): Observable<ISchoolInfo[]> {
        const self = this;
        
        // We already have the list. Return it as an observable.
        if (self._AvailableSchools && !!self._AvailableSchools.length) {
            let schools: ISchoolInfo[] = [];
            schools = self._AvailableSchools;
            return of(schools);

        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<ISchoolInfo[]>('Api/Services/MultiplePathways/GetAllSchools', {params: {
                AuthToken: AuthToken || '',
                IncludeCharter: IncludeCharter || ''
            }}).pipe(
                map((data) => {
                    const list = data || [];
                    self._AvailableSchools = list;
                    return list;
                }));
        }
    };

    // #endregion

    // #region Get All School Years Service Call

    GetAllSchoolYears(): Observable<string[]> {
        const self = this;

        // We already have the list. Return it as an observable.
        if (self._AllSchoolYears && !!self._AllSchoolYears.length) {
            let schoolyears: string[] = [];
            schoolyears = self._AllSchoolYears;
            return of(schoolyears);

        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<string[]>('Api/Services/MultiplePathways/GetAllSchoolYears')
                .pipe(
                    map((data) => {
                        const list = data || [];
                        self._AllSchoolYears = list;
                        return list;
                    }));
        }
    };

    // #endregion

    // #region Get Available Application School Years Service Call

    GetAvailableApplicationSchoolYears(): Observable<string[]> {
        const self = this;
            
        // We already have the list. Return it as an observable.
        if (self._AvailableApplicationSchoolYears && !!self._AvailableApplicationSchoolYears.length) {
            let appSchoolYears: string[] = [];
            appSchoolYears = self._AvailableApplicationSchoolYears;
            return of(appSchoolYears);
            
        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<string[]>('Api/Services/MultiplePathways/GetAvailableApplicationSchoolYears')
                .pipe(
                    map((data) => {
                        const list = data || [];
                        self._AvailableApplicationSchoolYears = list;
                        return list;
                    }));
        }
    }

    // #endregion

    // #region Get Available Challenge Types Service Call

    GetAvailableChallengeTypes(): Observable<IChallengeTypeInfo[]> {
        const self = this;

        if (self._AvailableChallengeTypes && !!self._AvailableChallengeTypes.length) {
            let challengeTypes: IChallengeTypeInfo[] = [];
            challengeTypes = self._AvailableChallengeTypes;
            return of(challengeTypes);

        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<IChallengeTypeInfo[]>('Api/Services/MultiplePathways/GetAvailableChallengeTypes')
                .pipe(
                    map((data) => {
                        const list = data.filter(d => d.Active) || [];
                        self._AvailableChallengeTypes = list;
                        return list;
                    }));
        }
    }

    // #endregion

    // #region Get Available Denial Types Service Call

    GetAvailableDenialTypes(): Observable<IDenialTypeInfo[]> {
        const self = this;

        if (self._AvailableDenialTypes && !!self._AvailableDenialTypes.length) {
            // We already have the list. Return it as an observable.
            let denialTypes: IDenialTypeInfo[] = [];
            denialTypes = self._AvailableDenialTypes;
            return of(self._AvailableDenialTypes);
            
        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<IDenialTypeInfo[]>('Api/Services/MultiplePathways/GetAvailableDenialTypes')
                .pipe(
                    map((data) => {
                        const list = data || [];
                        self._AvailableDenialTypes = list;
                        return list;
                    }));
        }
    }

    // #endregion

    // #region Get Available Programs Service Call

    GetAvailablePrograms(): Observable<IProgramInformation[]> {
        const self = this;

        if (self._AvailablePrograms && !!self._AvailablePrograms.length) {
            // We already have the list. Return it as an observable.
            let programs: IProgramInformation[] = [];
            programs = self._AvailablePrograms;
            return of(programs);
            
        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/GetAllPrograms')
                .pipe(
                    map((programs: IProgramInformation[]) => {
                        if (programs && !!programs.length) {
                            programs.forEach(p => {
                                p.OriginalProgramName = p.Description;
        
                                if (p.AgreementText) {
                                    p.AgreementText.DateCreated = p.AgreementText.DateCreated ? new Date(<unknown>p.AgreementText.DateCreated as string) : null;
                                }
                            });
                        }
                        self._AvailablePrograms = programs;
                        return programs;
                    }));
        }
    }

    // #endregion

    // #region Get Current Application School Year Service Call

    GetCurrentApplicationSchoolYear(): Observable<string> {
        const self = this;

        if (self._CurrentApplicationSchoolYear && !!self._CurrentApplicationSchoolYear.length) {
            // We already have the list. Return it as an observable.
            let schoolyear: string = '';
            schoolyear = self._CurrentApplicationSchoolYear;
            self.SetSelectedSchoolYear(schoolyear);
            return of(schoolyear);

        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<string>('Api/Services/MultiplePathways/GetCurrentApplicationYear')
            .pipe(
                map((data: string) => {
                    const year = data.toString() || '';
                    self._CurrentApplicationSchoolYear = year;
                    self.SetSelectedSchoolYear(year);
                    return year;
                }));
        }
    };

    // #endregion

    // #region Check Selected School Year with Current Application School Year

    SetSelectedSchoolYear(schoolyear: string | null) {
        // By default, start the app with this as the selected school year until it is changed by the user.
        if (this._SelectedSchoolYear == null ||
            this._SelectedSchoolYear == ""
        )
        this._SelectedSchoolYear = schoolyear;
    }

    // #endregion

    // #region Get Current School Year Service Call

    GetCurrentSchoolYear(): Observable<string> {
        const self = this;

        if (self._CurrentSchoolYear && !!self._CurrentSchoolYear.length) {
            // We already have the year. Return it as an observable.
            let currSchoolyear: string = '';
            currSchoolyear = self._CurrentSchoolYear;
            return of(currSchoolyear);

        } else {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<string>('Api/Services/MultiplePathways/GetCurrentSchoolYear')
                .pipe(
                    map((data: string) => {
                        const year = data.toString() || '';
                        self._CurrentSchoolYear = year;
                        return year;
                    }));
        }
    };

    // #endregion

    // #region Get Designees Service Call

    GetDesignees(AuthToken: string, CurrentRole: string, LocationNumber?: string | null) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            LocationNumber: LocationNumber || '',
        }

        return this.http.get<IDesigneeInfo[]>('Api/Services/MultiplePathways/GetDesignees', { params } )
            .pipe(map( (designees: IDesigneeInfo[]) => designees ));
    }

    // #endregion

    // #region Get Email Notification Customizations Service Call

    GetEmailNotificationCustomizations(AuthToken: string, CurrentRole: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
        }

        return this.http.get<IEmailNotificationCustomization[]>('Api/Services/MultiplePathways/GetEmailNotificationCustomizations', { params })
            .pipe(
                map( (results: IEmailNotificationCustomization[]) => {
                    // Run through return set and set the generic Required property to the correct entry based upon the current role.
                    if (results != null && results.length > 0) {
                        for (var i = 0, j = results.length; i < j; ++i) {
                            switch (CurrentRole) {
                                case 'parent': results[i].Required = results[i].ParentRequired; results[i].Frequency = results[i].Parent; break;
                                case 'student': results[i].Required = results[i].StudentRequired; results[i].Frequency = results[i].Student; break;
                                case 'designee': results[i].Required = results[i].DesigneeRequired; results[i].Frequency = results[i].Designee; break;
                                case 'principal': results[i].Required = results[i].PrincipalRequired; results[i].Frequency = results[i].Principal; break;
                                case 'systemadmin': results[i].Required = results[i].SystemAdminRequired; results[i].Frequency = results[i].SystemAdmin; break;
                                case 'programadmin': results[i].Required = results[i].ProgramAdminRequired; results[i].Frequency = results[i].ProgramAdmin; break;
                                case 'developer': results[i].Required = results[i].DeveloperRequired; results[i].Frequency = results[i].Developer; break;
                            }
                            if (results[i].Frequency === null) results[i].Frequency = 'O';
                        }
                    }

                    return results;
                } 
            ));
    }

    // #endregion

    // #region Get My Applications Service Call

    GetMyApplications(AuthToken: string, CurrentRole: string, SchoolNumber: string) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            SchoolNumber: SchoolNumber || ''
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetMyApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    // Go through the list of apps and use the filter tool to format the program name by including the program location (if any) in parens after it.
                    if (apps && !!apps.length) {
                        apps.forEach(d => {
                            // Update all application date fields and other fields as needed in the client.
                            self.ProcessApplicationFields(d);
                        });
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Get Notifications Service Call

    GetNotifications(AuthToken: string, AsRole?: string): Observable<INotificationsInfo | null> {
        const params = {
            AuthToken: AuthToken || '',
            AsRole: AsRole || ''
        }

        return this.http.get<INotificationsInfo>(this.baseUrl + 'Api/Services/Notification/GetNotifications', { params })
            .pipe(
                map( (notices: INotificationsInfo) => {
                    if (notices && notices.Notices && notices.Notices.length) {
                        notices.Notices.forEach(n => {
                            if (n.Details && n.Details.length) {
                                n.Details.forEach(d => {
                                    d.DateUpdated = (d.DateUpdated ? new Date(d.DateUpdated) : null);
                                });
                            }
                        });
                    }
                    return notices
                }),
            );
    }

    // #endregion

    // #region Get Pending Applications Service Call

    GetPendingApplications(AuthToken: string, CurrentRole: string) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || ''
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetPendingApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    // Go through the list of apps and use the filter tool to format the program name by including the program location (if any) in parens after it.
                    if (apps && !!apps.length) {
                        apps.forEach(d => {
                            // Update all application date fields and other fields as needed in the client.
                            self.ProcessApplicationFields(d);
                        });
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Get Program Contacts Service Call

    GetProgramContacts(AuthToken: string, CurrentRole: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || ''
        }

        return this.http.get<IProgramContactInformation[]>('Api/Services/MultiplePathways/GetProgramContacts', { params })
            .pipe(map( (admins: IProgramContactInformation[]) => admins));
    }

    // #endregion

    // #region Get Program Applications Service Call

    GetProgramApplications(AuthToken: string, CurrentRole: string, ProgramID?: number | null) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramID: ProgramID != null ? ProgramID : ''
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetProgramApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    if (apps && !!apps.length) {
                        apps.forEach(d => self.ProcessApplicationFields(d) );
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Get School Applications Service Call

    GetSchoolApplications(AuthToken: string, CurrentRole: string, SchoolNumber: string) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            SchoolNumber: SchoolNumber || ''
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetSchoolApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    if (apps && !!apps.length) {
                        apps.forEach(d => {
                            self.ProcessApplicationFields(d);
                        });
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Get Student Applications Service Call

    GetStudentApplications(AuthToken: string, CurrentRole: string, StudentID: string | null, SchoolYear: string | null = null) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            StudentID: StudentID || '',
            SchoolYear: SchoolYear || '',
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetStudentApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    if (apps && !!apps.length) {
                        apps.forEach(d => {
                            self.ProcessApplicationFields(d);
                        });
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Get Student Information Service Call

    GetStudentInformation(AuthToken: string, CurrentRole: string, StudentID: string | null) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            StudentID: StudentID || ''
        }

        return this.http.get<IExtendedStudentInfo>('Api/Services/MultiplePathways/GetStudentInformation', { params })
            .pipe(
                map( (result: IExtendedStudentInfo) => {
                    if (result && result.HomePhone) {
                        result.HomePhone = (result.HomePhone || '').replace(/\D/g, '');
                    }

                    return result;
                } 
            ));
    };

    // #endregion

    // #region Get User Program Contacts Service Call

    GetUserProgramContacts(AuthToken: string, CurrentRole: string) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
        }

        // if (!self._ProgramContacts) {
            // We do not have what we need loaded yet. Load it now.
            return self.http.get<number[]>('Api/Services/MultiplePathways/GetUserProgramContacts', { params })
                .pipe(
                    map((programContacts: number[]) => programContacts),
                    // tap((programContacts: number[]) => {
                    //     self._ProgramContacts = programContacts;
                    // })
                );
        // } 
        // else {
        //     // We already have the list. Return it as an observable.
        //     return of<number[]>(self._ProgramContacts);
        // }

        // return this.http.get<number[]>('Api/Services/MultiplePathways/GetUserProgramContacts', { params })
        //     .pipe(map( (result: number[]) => result));
    };

    // #endregion

    // #region Get Wait List Applications Service Call

    GetWaitListApplications(AuthToken: string, CurrentRole: string) {
        const self = this;
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
        }

        return self.http.get<IStudentApplication[]>('Api/Services/MultiplePathways/GetWaitListApplications', { params })
            .pipe(
                map( (apps: IStudentApplication[]) => {
                    // Go through the list of apps and update the application fields.
                    if (apps && !!apps.length) {
                        apps.forEach(d => {
                            self.ProcessApplicationFields(d);
                        });
                    }

                    return apps;
                } 
            ));
    }

    // #endregion

    // #region Process Application Fields

    ProcessApplicationFields(d: IStudentApplication) {
        if (d == null) { return; }

        d.DateCreated = d.DateCreated ? new Date(<unknown>d.DateCreated as string) : null;
        d.DateResolved = d.DateResolved ? new Date(<unknown>d.DateResolved as string) : null;
        d.DateUpdated = d.DateUpdated ? new Date(<unknown>d.DateUpdated as string) : null;
        d.ProgramStartDate = d.ProgramStartDate ? new Date(<unknown>d.ProgramStartDate as string) : null;
        d.RequestInfoDate = d.RequestInfoDate ? new Date(<unknown>d.RequestInfoDate as string) : null;
        d.StudentDOB = d.StudentDOB ? new Date(<unknown>d.StudentDOB as string) : null;

        if (d.ProgramsApplied && !!d.ProgramsApplied.length) {
            d.ProgramsApplied.forEach(pa => {
                if (d.ProgramLocation && !!pa.Approved) pa.ProgramName += ' (' + d.ProgramLocation + ')';
            });

            // Sort by listing the program that was approved (if any) first, then all denied by alpha (if any)
            d.ProgramsApplied = d.ProgramsApplied.sort((a, b) => {
                if (a.Approved === true) return -1; // If first is approved, sort early.
                if (b.Approved === true) return 1; // If second is approved, sort early.
                return a.ProgramName!.localeCompare(b.ProgramName!);
            });
        }

        var fStatus = this.formstatuspipe.transform(d.FormStatus!);
        d.FormStatusText = fStatus;
    }

    // #endregion

    // #region Reactivate Application Service Call

    ReactivateApplication(AuthToken: string, CurrentRole: string, PathwaysReferralID: number) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || ''
        }
        return this.http.get<number>('Api/Services/MultiplePathways/ReactivateApplication', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region Reinstate Program Service Call

    ReinstateProgram(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ReferralProgramChoiceID: number) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || '',
            ReferralProgramChoiceID: ReferralProgramChoiceID || ''
        }

        return this.http.get<number>('Api/Services/MultiplePathways/ReinstateProgram', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region Remove Application Service Call

    RemoveApplication(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, Note: string) {
        const body = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID || '',
            Reason: Note || ''
        }
        
        return this.http.post<boolean>('Api/Services/MultiplePathways/RemoveApplication', body )
            .pipe(map( (success: boolean) => success));
    }

    // #endregion

    // #region Remove All Pending Applications Service Call

    RemoveAllPendingApplications(AuthToken: string, CurrentRole: string, PathwaysReferralIDList: number[], Note: string) {
        const body = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralIDList: PathwaysReferralIDList,
            Reason: Note || ''
        }
        
        return this.http.post<boolean>('Api/Services/MultiplePathways/RemoveAllPendingApplications', body )
            .pipe(map( (success: boolean) => success));
    }

    // #endregion

    // #region Remove Clearinghouse Team Member Service Call

    RemoveClearinghouseTeamMember(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        }
        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/RemoveClearinghouseTeamMember', { params })
            .pipe(map( (members: IClearinghouseInfo[]) => members));
    }

    // #endregion

    // #region Remove Designee Service Call

    RemoveDesignee(AuthToken: string, CurrentRole: string, DesigneeID?: number | null) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            DesigneeID: DesigneeID || ''
        };

        return this.http.get<IDesigneeInfo[]>('Api/Services/MultiplePathways/RemoveDesignee', { params })
            .pipe(
                map( (designees: IDesigneeInfo[]) => {
                    if (designees && !!designees.length) {
                        designees.forEach(d => {
                            d.CreatedDate = d.CreatedDate ? new Date(<unknown>d.CreatedDate as string) : null;
                            d.ExpirationDate = d.ExpirationDate ? new Date(<unknown>d.ExpirationDate as string) : null;
                        });
                    }

                    return designees;
                } 
            ));
    }

    // #endregion

    // #region Remove District Staff Member Service Call

    RemoveDistrictStaffMember(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        }

        
        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/RemoveDistrictStaffMember', { params })
            .pipe(map( (staffMemberList: IClearinghouseInfo[]) => staffMemberList));
    }

    // #endregion

    // #region Remove Program Service Call

    RemoveProgram(AuthToken: string, CurrentRole: string, ProgramID: number) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramID: ProgramID || ''
        }

        return this.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/RemoveProgram', { params })
            .pipe(
                map( (programs: IProgramInformation[]) => {
                    if (programs && !!programs.length) {
                        programs.forEach(p => {
                            p.OriginalProgramName = p.Description;
                        });
                    }

                    return programs;
                } 
            ));
    }

    // #endregion

    // #region Remove Program Contact Service Call

    RemoveProgramContact(AuthToken: string, CurrentRole: string, ProgramContactID?: number) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramContactID: ProgramContactID || ''
        }

        return this.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/RemoveProgramContact', { params })
            .pipe(map( (programContactList: IProgramInformation[]) => programContactList));
    }

    // #endregion

    // #region Remove Reader Service Call

    RemoveReader(AuthToken: string, CurrentRole: string, UserID?: number, SAPID?: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            UserID: UserID || '',
            SAPID: SAPID || ''
        };

        return this.http.get<IClearinghouseInfo[]>('Api/Services/MultiplePathways/RemoveReader', { params })
            .pipe(map( (clearlingList: IClearinghouseInfo[]) => clearlingList));
    }

    // #endregion

    // #region Search For Student Service Call

    SearchForStudent(AuthToken: string, SchoolYear?: string, SchoolNumber?: string, StudentID?: string, GradeLevel?: string, FirstName?: string, LastName?: string) {
        const httpParams = {
            AuthToken: AuthToken,
            SchoolYear: SchoolYear,
            SchoolNumber: SchoolNumber,
            StudentID: StudentID,
            GradeLevel: GradeLevel,
            FirstName: FirstName,
            LastName: LastName
        };

        return this.http.post<ISearchStudentResult[]>('Api/Services/MultiplePathways/SearchForStudent', httpParams)
            .pipe(map( (students: ISearchStudentResult[]) => students));
    };

    // #endregion

    // #region Search For User Service Call

    SearchForUser(AuthToken: string, CurrentRole: string, FirstName?: string | null, LastName?: string | null, SAPID?: string | null, ForcedLocationNumber?: string | null): Observable<IUserSearchResult[]> {
        return this.http.post<IUserSearchResult[]>('Api/Services/MultiplePathways/SearchForUser', {
            AuthToken: AuthToken,
            CurrentRole: CurrentRole,
            FirstName: FirstName,
            LastName: LastName,
            SAPID: SAPID,
            SchoolNumber: ForcedLocationNumber
        })
            .pipe(map( (results: IUserSearchResult[]) => results));
    };

    // #endregion
    
    // #region Send To Wait List Service Call

     SendToWaitList(AuthToken: string, CurrentRole: string, PathwaysReferralID: number) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID
        };

        return this.http.get<number>('Api/Services/MultiplePathways/SendToWaitList', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region System Admin Update Approval Type Service Call

    SystemAdminUpdateApprovalType(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ProgramStartDate: Date, ProgramLocation: string, Conditional: boolean, ConditionalNote: string) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: PathwaysReferralID,
            ProgramStartDate: ProgramStartDate,
            ProgramLocation: ProgramLocation,
            Conditional: Conditional,
            ConditionalNote: ConditionalNote
        };

        return this.http.post<number>('Api/Services/MultiplePathways/SystemAdminUpdateApprovalType', { params })
            .pipe(map( (referralID: number) => referralID));
    }

    // #endregion

    // #region System Admin Upsert Student Application Service Call

    SystemAdminUpsertStudentApplication(AuthToken: string, CurrentRole: string, PostAction: number, PathwaysReferralID: number, StudentID: string, Grade: string, SchoolYear: string, ReferralSchoolNumber: string, HomeSchoolNumber: string, ApplicationTypeID: number, RecommendedProgramID: number, RecommendedProgramLength: number, ProgramStartDate: Date, ProgramLocation: string, ParentInitialProxyName: string, ParentInitialProxyTypeID: number, ParentInitialProxyTypeNote: string, ConditionalNote : string, DenialTypeID: number, DenialNote: string, ESELocked: boolean, HasInternet: boolean, Challenges: number[]) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PostAction: PostAction,
            PathwaysReferralID: PathwaysReferralID,
            StudentID: StudentID,
            Grade: Grade,
            SchoolYear: SchoolYear,
            ReferralSchoolNumber: ReferralSchoolNumber,
            HomeSchoolNumber: HomeSchoolNumber,
            ApplicationTypeID: ApplicationTypeID,
            RecommendedProgramID: RecommendedProgramID,
            RecommendedProgramLength: RecommendedProgramLength,
            ProgramStartDate: ProgramStartDate,
            ProgramLocation: ProgramLocation,
            ParentInitialProxyName: ParentInitialProxyName,
            ParentInitialProxyTypeID: ParentInitialProxyTypeID,
            ParentInitialProxyTypeNote: ParentInitialProxyTypeNote,
            ConditionalNote: ConditionalNote,
            DenialTypeID: DenialTypeID,
            DenialNote: DenialNote,
            ESELocked: ESELocked,
            HasInternet: HasInternet,
            Challenges: Challenges
        };

        return this.http.post<number>('Api/Services/MultiplePathways/SystemAdminUpsertStudentApplication', { params })
            .pipe(map( (studentapps: number) => studentapps));
    }

    // #endregion        

    // #region Update Email Notification Customizations Service Call

    UpdateEmailNotificationCustomizations(AuthToken: string, CurrentRole: string, UserChoices: IEmailNotificationCustomizationChoice[]) {
        const params = {
            AuthToken: AuthToken,
            CurrentRole: CurrentRole,
            UserChoices: UserChoices
        };

        return this.http.post<IEmailNotificationCustomization[]>('Api/Services/MultiplePathways/UpdateEmailNotificationCustomizations', { params })
            .pipe(
                map( (choices: IEmailNotificationCustomization[] | null) => {
                    // Run through return set and set the generic Required property to the correct entry based upon the current role.
                    if (choices != null && choices.length > 0) {
                        for (var i = 0, j = choices.length; i < j; ++i) {
                            switch (CurrentRole) {
                                case 'parent': choices[i].Required = choices[i].ParentRequired; choices[i].Frequency = choices[i].Parent; break;
                                case 'student': choices[i].Required = choices[i].StudentRequired; choices[i].Frequency = choices[i].Student; break;
                                case 'designee': choices[i].Required = choices[i].DesigneeRequired; choices[i].Frequency = choices[i].Designee; break;
                                case 'principal': choices[i].Required = choices[i].PrincipalRequired; choices[i].Frequency = choices[i].Principal; break;
                                case 'systemadmin': choices[i].Required = choices[i].SystemAdminRequired; choices[i].Frequency = choices[i].SystemAdmin; break;
                                case 'programadmin': choices[i].Required = choices[i].ProgramAdminRequired; choices[i].Frequency = choices[i].ProgramAdmin; break;
                                case 'developer': choices[i].Required = choices[i].DeveloperRequired; choices[i].Frequency = choices[i].Developer; break;
                            }
                            if (choices[i].Frequency === null) choices[i].Frequency = 'O';
                        }
                    }
                    return choices;
                }),
            );
    }

    // #endregion

    // #region Update ESE Lock Status Service Call

    UpdateESELock(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, LockStatus: boolean) {
        const params = {
            AuthToken: AuthToken,
            PathwaysReferralID: PathwaysReferralID,
            CurrentRole: CurrentRole,
            LockStatus: LockStatus
        };

        return this.http.get<number>('Api/Services/MultiplePathways/UpdateESELock', { params })
            .pipe(map( (ESELuckSuccess: number | null) => ESELuckSuccess));
    }

    // #endregion

    // #region Update Location Service Call

    UpdateLocation(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ProgramStartDate: Date, ProgramLocation: string) {
        const params = {
            AuthToken: AuthToken,
            PathwaysReferralID: PathwaysReferralID,
            CurrentRole: CurrentRole,
            ProgramStartDate: ProgramStartDate.toDateString(), //TODO: check if toDateString() works with server code
            ProgramLocation: ProgramLocation
        }

        return this.http.get<boolean>('Api/Services/MultiplePathways/UpdateLocation', { params })
            .pipe(map( (success: boolean) => success));
    }

    // #endregion

    // #region Update Parent/Guardian Contact Info Service Call

    UpsertParentGuardian(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ParentGuardianID: number, ParentGuadian: IParentGuardian): Observable<boolean> {
        const params = {
            AuthToken: AuthToken,
            CurrentRole: CurrentRole,
            PathwaysReferralID: PathwaysReferralID,
            ParentGuardianID: ParentGuardianID || '',
            StudentID: ParentGuadian.StudentID || '',
            FirstName: ParentGuadian.FirstName || '',
            LastName: ParentGuadian.LastName || '',
            Email: ParentGuadian.Email || '',
            Phone: ParentGuadian.Phone || ''
        }

        return this.http.get<boolean>('Api/Services/MultiplePathways/UpsertParentGuardian', { params })
            .pipe(map( (success: boolean) => success));
    }

    // #endregion

    // #region Update Program Service Call

    UpdateProgram(AuthToken: string, CurrentRole: string, PathwaysReferralID: number, ProgramID: number) {
        const params = {
            AuthToken: AuthToken,
            PathwaysReferralID: PathwaysReferralID,
            CurrentRole: CurrentRole,
            ProgramID: ProgramID
        };

        return this.http.get<number>('Api/Services/MultiplePathways/UpdateProgram', { params })
            .pipe(map( (success: number) => success));
    }

    // #endregion
    
    // #region Update Program Contact Service Call

    UpdateProgramContact(AuthToken: string, CurrentRole: string, ProgramContactID?: number, LocationNumber?: string, PrimaryContact?: boolean) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramContactID: ProgramContactID || '',
            LocationNumber: LocationNumber || '',
            PrimaryContact: PrimaryContact != null ? PrimaryContact : ''
        };

        return this.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/UpdateProgramContact', { params })
            .pipe(
                map( (pgms: IProgramInformation[]) => {
                    if (pgms && !!pgms.length) {
                        pgms.forEach(p => {
                            p.OriginalProgramName = p.Description;
    
                            if (p.AgreementText) {
                                p.AgreementText.DateCreated = p.AgreementText.DateCreated ? new Date(p.AgreementText.DateCreated.toString()) : null;
                            }
                        });
                    }

                    return pgms;
                } 
            ));
    }

    // #endregion

    // #region Update Program Location Service Call

    UpdateProgramLocation(AuthToken: string, CurrentRole: string, PathwaysReferralID: number | null | undefined,  ProgramLocation: string) {
        const params = {
            AuthToken: AuthToken || '',
            PathwaysReferralID: PathwaysReferralID || '',
            CurrentRole: CurrentRole || '',
            ProgramLocation: ProgramLocation || ''
        };

        return this.http.get<boolean>('Api/Services/MultiplePathways/UpdateProgramLocation', { params })
            .pipe(map( (success: boolean) => success));
    }

    // #endregion

    // #region Upsert Designee Service Call

    UpsertDesignee(AuthToken: string, CurrentRole: string, DesigneeID: number, UserID: number, SAPID: string, PositionNo: string, LocationNumber: string, ExpirationDate?: Date) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            DesigneeID: DesigneeID || '',
            UserID: UserID || '',
            SAPID: SAPID || '',
            PositionNo: PositionNo || '',
            LocationNumber: LocationNumber || '',
            ExpirationDate: ExpirationDate?.toISOString() || ''
        };

        return this.http.get<IDesigneeInfo[]>('Api/Services/MultiplePathways/UpsertDesignee', { params })
            .pipe(
                map( (designees: IDesigneeInfo[]) => {
                    if (designees && !!designees.length) {
                        designees.forEach(d => {
                            d.CreatedDate = d.CreatedDate ? new Date(d.CreatedDate) : null;
                            d.ExpirationDate = d.ExpirationDate ? new Date(d.ExpirationDate) : null;
                        });
                    }

                    return designees;
                } 
            ));
    }

    // #endregion

    // #region Upsert Program Service Call

    UpsertProgram(AuthToken: string, CurrentRole: string, ProgramID?: number, ProgramName?: string, SortOrder?: number, Active?: boolean) {
        const params = {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            ProgramID: ProgramID || '',
            ProgramName: ProgramName || '',
            SortOrder: SortOrder || '',
            Active: (Active != null ? Active : '')
        };

        return this.http.get<IProgramInformation[]>('Api/Services/MultiplePathways/UpsertProgram', { params })
            .pipe(
                map( (programs: IProgramInformation[]) => {
                    if (programs && !!programs.length) {
                        programs.forEach(p => {
                            p.OriginalProgramName = p.Description;
                        });
                    }

                    return programs;
                } 
            ));
    }

    // #endregion

    // #region Upsert Student Application Service Call

    UpsertStudentApplication(AuthToken: string, CurrentRole: string, Application: IStudentApplication) {
        let headers = new HttpHeaders({'Content-type': 'application/json'});

        return this.http.post('Api/Services/MultiplePathways/UpsertStudentApplication', {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            PathwaysReferralID: Application.PathwaysReferralID || null,
            StudentID: Application.StudentID || '',
            Grade: Application.Grade || '',
            StudentInfoSchoolYear: Application.StudentInfoSchoolYear || '',
            ReferralSchoolNumber: Application.ReferralSchoolNumber || '',
            HomeSchoolNumber: Application.HomeSchoolNumber || '',
            FormStatus: Application?.FormStatus?.toString() || '',
            ProgramStartDate: Application.ProgramStartDate != null ? Application.ProgramStartDate.toJSON() : null,
            ProgramLocation: Application.ProgramLocation || '',
            CreatorNote: Application.CreatorNote || '',
            ESELocked: Application.ESELocked != null ? Application.ESELocked : '',
            HasInternet: Application.HasInternet != null ? Application.HasInternet : null,
            Challenges: Application.Challenges || '',
            ProgramsApplied: Application.ProgramsApplied || '',
            ParentGuardian: Application.ParentGuardian ?? null,
        }, {headers} )
            .pipe(map( (numbeofapps) => { return numbeofapps }));
    }

    // #endregion
}